import React from 'react';
import { useIntl } from 'react-intl';
import { Dialog, DialogTitle, IconButton, Button } from '@mui/material';
import styled from '@emotion/styled';
import { image } from 'src/theme';
import { DangerouslyHtml } from '@/components/item';
import { LoadingSpinner } from '@/components/loading';
import { useGetBasicPage } from '@/hooks/CommonRequests';
import { mq } from '@/utils/mediaQuery';
import { SignupStyledDialogContent } from './inquryStyles';

const CustomCheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M8 12L11 15L16 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TermsModalDialog = styled(Dialog)(() => ({
  overflowX: 'hidden',
  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '400px',
    marginLeft: 0,
    marginRight: 0,
    borderRadius: '12px',
  },

  [mq('tablet')]: {
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: '800px',
    },
  },
}));

const ModalHeader = styled(DialogTitle)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  fontWeight: 'bold',
  fontSize: '1.6rem',

  [mq('tablet')]: {
    fontSize: '1.8rem',
  },
}));

const TermsModalContent = styled(SignupStyledDialogContent)(() => ({
  position: 'relative',
  maxHeight: '70vh',
  overflowY: 'auto',

  lineHeight: '1.6',
  wordBreak: 'keep-all',

  '& ul, & li, & ol': {
    listStyle: 'revert',
  },

  '& h4': {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    marginTop: '1.2rem',
    color: '#222',
  },

  '& h4:first-of-type': {
    marginTop: 0,
  },

  '& p': {
    fontSize: '1.2rem',
    margin: 0,
    color: '#555',
  },

  [mq('tablet')]: {
    '& h4': {
      fontSize: '1.6rem',
    },

    '& p': {
      fontSize: '1.4rem',
    },
  },
}));

export const TermsOverlay = styled.div(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
}));

const AgreeButtonWrapper = styled.div(() => ({
  padding: '24px',
  display: 'flex',
  justifyContent: 'center',
}));

const AgreeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.color.primary_color,
  color: '#fff',
  width: '100%',
  height: '4.2rem',
  padding: '16px',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.color.primary_color,
  },
  [mq('tablet')]: {
    width: '100%',
    height: '100%',
    fontSize: '1.8rem',
  },
}));

const InquiryTermsModal = ({ isOpen, onClose, alias, type, onAgree }) => {
  const intl = useIntl();

  // 하드코딩된 다국어 데이터를 사용해야 하는 조건인지 판별
  const isCustomPrivacyPolicy =
    alias === 'private-policy' &&
    (type === 'arena_inquiry' || type === 'convention_facilities_inquiry');

  // 커스텀 정책일 경우 API 호출을 방지(enabled: false)
  const { data: getBasicPageInformation, isFetching } = useGetBasicPage(alias, {
    enabled: isOpen && !!alias && !isCustomPrivacyPolicy,
  });

  // 타입에 따른 다국어 타이틀 및 컨텐츠 매핑
  const getCustomContent = () => {
    if (type === 'arena_inquiry') {
      return {
        title: intl.formatMessage({ id: 'terms-privacy-arena-title' }),
        content: intl.formatMessage({ id: 'terms-privacy-arena-content' }),
      };
    }
    if (type === 'convention_facilities_inquiry') {
      return {
        title: intl.formatMessage({ id: 'terms-privacy-convention-title' }),
        content: intl.formatMessage({ id: 'terms-privacy-convention-content' }),
      };
    }
    return { title: '', content: '' };
  };

  const customData = isCustomPrivacyPolicy ? getCustomContent() : null;

  // 화면에 노출할 최종 데이터 결정
  const displayTitle = isCustomPrivacyPolicy
    ? customData.title
    : getBasicPageInformation?.title || '\u200b';

  const displayContent = isCustomPrivacyPolicy
    ? customData.content
    : getBasicPageInformation?.field_content_bottom || getBasicPageInformation?.body || '';

  return (
    <TermsModalDialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableScrollLock
    >
      <ModalHeader>
        {displayTitle}
        <IconButton onClick={onClose} size="large" aria-label="close">
          <img src={image.close.default} alt="close icon" />
        </IconButton>
      </ModalHeader>

      <TermsModalContent dividers>
        {isFetching && !isCustomPrivacyPolicy ? (
          <TermsOverlay>
            <LoadingSpinner />
          </TermsOverlay>
        ) : (
          <DangerouslyHtml value={displayContent} />
        )}
      </TermsModalContent>

      {(!isFetching || isCustomPrivacyPolicy) && (
        <AgreeButtonWrapper>
          <AgreeButton onClick={onAgree} startIcon={<CustomCheckIcon />}>
            {intl.formatMessage({ id: 'lang-agree' })}
          </AgreeButton>
        </AgreeButtonWrapper>
      )}
    </TermsModalDialog>
  );
};

export default InquiryTermsModal;
